/**
 * checkoutOwnership.ts — what the checkout says when orders refuses a SIGNED-IN
 * patient on ownership grounds (orders S0.4, #254, 2026-09-25).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: checkout email hold on the
 * authed lanes (2026-09-26).
 *
 * THE SERVER RULE (orders `checkout-bind-ownership.ts`, `decideAccountLaneOwnership`).
 * A signed-in caller may create or advance a checkout case on an assessment only
 * when clinic says the assessment is already bound to them, or their account is
 * VERIFIED and its email is the Lead's. The three authed lanes this portal calls
 * ask it: `POST checkout/selection` (the plan save), `POST checkout/session` (the
 * session, minted on the safety tick) and the attach
 * (`POST checkout/:id/payment-method`). The refusal is a 403 whose whole Nest
 * `message` is one of two stable, PHI-free codes:
 *  - `email_not_verified` — the caller's OWN state, asked before the Lead so it
 *    is no oracle; the same code identity's EmailVerifiedGuard and identity#105's
 *    bind refusal send, so ONE constant answers every service's "confirm your
 *    email first" (the account step's bind hold reads it from here too);
 *  - `checkout_not_owner` — every other refusal, never saying which (a different
 *    Lead email, no Lead email, an unknown assessment, bound to somebody else).
 * The guest lanes (`/checkout/guest/*`) are grant-checked and never ask it.
 *
 * WHERE THE 403 LANDS ON THE SERVER, verified on orders `origin/dev` f35b171:
 *  - selection and session — `assertAccountLaneOwnership` is the FIRST thing the
 *    service does, before any read of its own, any write, and any Stripe call;
 *  - attach — after Phase A (a short DB transaction: the idempotent replay exit,
 *    the case/acknowledgement checks) and BEFORE Phase B, i.e. before
 *    `retrieveSetupIntent`, the customer and the month-1 hold. So THAT request
 *    moves no money. ⚠️ It says nothing about an EARLIER attempt: Phase A also
 *    serves the `awaiting_approval` orphan repair (a hold placed at Stripe whose
 *    ledger write failed) and a retry after a lost answer. Hence the rule below.
 *
 * ⚠️ MONEY: THE HOLD SAYS NOTHING ABOUT MONEY, ON ANY LANE. Not "not charged",
 * not "your order is placed". On the attach lane a hold from an earlier attempt
 * may stand, and the copy on this screen must be true whatever the card carries.
 * And the way on is the SAME request: the plan save re-sent, the session re-POSTed,
 * the SAME SetupIntent re-attached — never a second Stripe `confirmSetup` (the
 * card was confirmed; orders answers a re-attach of the same intent idempotently).
 *
 * EXACT STATUS AND EXACT CODE. A looser match (a regex on `detail`, or any 403)
 * would dress an unrelated refusal up as "just confirm your email" and the
 * patient would wait on a link that can never unlock the step. Anything else —
 * an older orders build with no S0.4, or a 403 with other words — keeps the
 * handling it had before this file existed.
 */
import { ApiError } from '../api/client';
import { CONFIRMED_CONTINUE_LABEL } from '../auth/CheckEmailCard';

/** identity's EmailVerifiedGuard code, and orders S0.4's first refusal (verbatim). */
export const EMAIL_NOT_VERIFIED = 'email_not_verified';

/** orders S0.4's every-other-reason refusal (verbatim, `CHECKOUT_NOT_OWNER`). */
export const CHECKOUT_NOT_OWNER = 'checkout_not_owner';

export type CheckoutOwnershipRefusal = typeof EMAIL_NOT_VERIFIED | typeof CHECKOUT_NOT_OWNER;

/**
 * Is this failure orders S0.4 refusing the signed-in caller? The code, or null
 * for anything else (which keeps its existing handling).
 */
export function checkoutOwnershipRefusal(err: unknown): CheckoutOwnershipRefusal | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  if (err.detail === EMAIL_NOT_VERIFIED) return EMAIL_NOT_VERIFIED;
  if (err.detail === CHECKOUT_NOT_OWNER) return CHECKOUT_NOT_OWNER;
  return null;
}

/* ── The words ─────────────────────────────────────────────────────────────
 * Calm, and free of every phrase a failure uses: no "Sorry", no "please try
 * again", no "link expired" — the step did not fail, it is waiting on something
 * the patient can do. Pinned by `checkoutOwnership.spec.tsx`. */

/** The email hold's heading (the task's words, CTO brief 2026-09-26). */
export const EMAIL_HOLD_TITLE = 'Confirm your email to continue';

/**
 * The line under it, around the masked address (rendered by the hold, which
 * owns the accessible form of the address). "We sent", not "we've sent": a
 * patient who signed in to an account made days ago was sent their link then,
 * and a sign-in sends none — the resend hint on the card covers the gone link.
 */
export const EMAIL_HOLD_LINE_BEFORE = 'We sent a link to ';
export const EMAIL_HOLD_LINE_AFTER = `. Open it to confirm your email, then come back to this page and press “${CONFIRMED_CONTINUE_LABEL}”.`;
/** The same line when the session carries no address (an older identity `/auth/me`). */
export const EMAIL_HOLD_LINE_NO_ADDRESS = `We sent a link to your email address. Open it to confirm your email, then come back to this page and press “${CONFIRMED_CONTINUE_LABEL}”.`;

/**
 * After "I've confirmed it — continue", when orders STILL says the mailbox is
 * unconfirmed. Says what to do next, in the patient's terms; never "try again".
 */
export const EMAIL_HOLD_NOT_YET = `We can’t see your email as confirmed yet. Open the link in the email we sent, then press “${CONFIRMED_CONTINUE_LABEL}”.`;

/* ── `email_not_verified` while the account's own session says VERIFIED ─────
 * (review, 2026-09-26). orders' "verified" is `isProvenMailbox` on identity's
 * dispensing-profile, not `/auth/me`: an identity older than S0.3 (no
 * `emailVerified` on that read — the rollback floor orders' CLAUDE.md records
 * for S0.3 → S0.4), a blank Patient email, or identity being unavailable all
 * come back as `email_not_verified` for a patient whose session says their
 * email is confirmed. The email hold's words would then be false — "confirm
 * your email", "we can't see it as confirmed yet", "a new link is on its way"
 * (identity's resend sends nothing to a verified account) — and nothing the
 * patient does in their inbox can end it. So: no mailbox words, no resend, the
 * truth and a way to reach us, and one Continue that re-sends the same step
 * (identity may only have been unavailable for a moment). Still calm: nothing
 * about money, never the danger tone, never "try again". */

/** The account-check screen's heading. */
export const ACCOUNT_CHECK_TITLE = 'We couldn’t confirm your account just now';
/** The account-check screen's line: true whatever made orders say no. */
export const ACCOUNT_CHECK_BODY =
  'Your email is already confirmed, so there’s nothing more to do in your inbox. Please contact us and we’ll help you finish your order.';
/** Its one control: re-read the session and re-send the same step. */
export const ACCOUNT_CHECK_CONTINUE = 'Continue';
/** The status line when orders says the same again after that Continue. */
export const ACCOUNT_CHECK_STILL = 'We still couldn’t confirm your account. Please contact us and we’ll help you finish your order.';

/**
 * `checkout_not_owner`: the heading (the task's first sentence, as a heading).
 *
 * ⚠️ NOT TRUE FOR EVERY REASON behind the code (review, 2026-09-26): orders
 * sends `checkout_not_owner` for no Lead email and an unknown assessment too,
 * and never says which (non-enumerating, by design). The words are the CTO
 * brief's own and cover the common case — a Lead email that is not this
 * account's — so they are kept; a heading that holds for every reason is a
 * copy decision for the CTO, raised in the PR, not made here. "Start again"
 * works for all of them; "Sign in with that email" only where a Lead email
 * exists.
 */
export const NOT_OWNER_TITLE = 'This order was started with a different email address';
/** `checkout_not_owner`: the way on, in words, above its two controls. */
export const NOT_OWNER_BODY = 'Sign in with that email, or start a new check.';
export const NOT_OWNER_SIGN_OUT = 'Sign out';
export const NOT_OWNER_START_AGAIN = 'Start again';
